import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getUserTickets } from "../../firebase";
import { Ticket } from "../../types";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  TicketIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  UsersIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

const UserTickets: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTickets, setExpandedTickets] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const fetchTickets = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userTickets = await getUserTickets(currentUser.uid);
        // Sort tickets by date, most recent purchases first
        setTickets(
          userTickets.sort(
            (a, b) =>
              new Date(b.purchaseDate).getTime() -
              new Date(a.purchaseDate).getTime()
          )
        );
      } catch (err) {
        console.error("Error loading tickets:", err);
        setError("Failed to load your tickets. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [currentUser]);

  const formatDate = (dateStr: string | any) => {
    try {
      // Handle Firestore timestamp or ISO string
      if (typeof dateStr === "object" && dateStr && "toDate" in dateStr) {
        return format(dateStr.toDate(), "MMM d, yyyy");
      }
      return format(parseISO(dateStr), "MMM d, yyyy");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateStr;
    }
  };

  const toggleTicketDetails = (ticketId: string) => {
    setExpandedTickets((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <div className="text-center py-12">
        <TicketIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          No tickets found
        </h3>
        <p className="mt-1 text-gray-500">
          You haven't purchased any event tickets yet.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="space-y-4">
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            className="bg-white rounded-lg shadow-md p-4 sm:p-6"
          >
            <div className="flex flex-col sm:flex-row justify-between">
              <div>
                <h3 className="text-lg font-bold text-gold">
                  {ticket.eventTitle}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Purchased on {formatDate(ticket.purchaseDate)}
                </p>
              </div>
              <div className="mt-2 sm:mt-0 flex items-center">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    ticket.status === "active"
                      ? "bg-green-100 text-green-800"
                      : ticket.status === "used"
                      ? "bg-gray-100 text-gray-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {ticket.status.charAt(0).toUpperCase() +
                    ticket.status.slice(1)}
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {ticket.eventDate && (
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
                  <span>{ticket.eventDate}</span>
                </div>
              )}
              {ticket.eventTime && (
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
                  <span>{ticket.eventTime}</span>
                </div>
              )}
              {ticket.eventLocation && (
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-gray-500" />
                  <span>{ticket.eventLocation}</span>
                </div>
              )}
            </div>

            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                  <span className="font-medium">Primary Attendee</span>
                </div>
                <button
                  onClick={() => toggleTicketDetails(ticket.id)}
                  className="text-gold hover:text-gold flex items-center text-sm"
                >
                  {expandedTickets[ticket.id] ? (
                    <>
                      Hide Details
                      <ChevronUpIcon className="h-4 w-4 ml-1" />
                    </>
                  ) : (
                    <>
                      Show Details
                      <ChevronDownIcon className="h-4 w-4 ml-1" />
                    </>
                  )}
                </button>
              </div>

              {expandedTickets[ticket.id] && (
                <div className="mt-4 space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                      <span className="font-medium">Attendee Information</span>
                    </div>
                    <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-gray-500">Name: </span>
                        <span>{ticket.name}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Email: </span>
                        <span>{ticket.email}</span>
                      </div>
                      {ticket.company && (
                        <div className="md:col-span-2">
                          <span className="text-sm text-gray-500">
                            Company:{" "}
                          </span>
                          <span>{ticket.company}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="font-medium">Payment Details</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <span className="text-sm text-gray-500">
                          Payment ID:{" "}
                        </span>
                        <span className="font-medium">{ticket.paymentId}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                className="bg-gray-100 hover:bg-blue-200 text-gray-600 px-4 py-2 rounded text-sm"
                onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
              >
                View Ticket
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserTickets;
